import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Eye, ArrowRight, Bookmark, Share2 } from 'lucide-react';
import type { NewsArticleWithRelations } from '../../../types/news';

interface NewsHeroProps {
  featuredArticles: NewsArticleWithRelations[];
  currentSlide: number;
}

const NewsHero: React.FC<NewsHeroProps> = ({ featuredArticles, currentSlide }) => {
  if (featuredArticles.length === 0) return null;
  
  const article = featuredArticles[currentSlide];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.3]);
  const scale = useTransform(scrollY, [0, 300], [1, 1.1]);
  
  return (
    <motion.div 
      className="relative mb-16 overflow-hidden rounded-3xl h-[80vh] group"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div 
        className="absolute inset-0 w-full h-full"
        style={{ y, scale }}
      >
        <motion.img 
          src={article.cover_image} 
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.8 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
      </motion.div>
      
      <motion.div 
        className="relative z-20 h-full flex items-end p-8 md:p-12"
        style={{ opacity }}
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap items-center gap-4 mb-6"
          >
            <span className="px-4 py-2 bg-primary/90 text-primary-foreground rounded-full text-sm font-medium">
              {article.category?.name || 'News'}
            </span>
            <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(article.published_at || '').toLocaleDateString()}
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight gradient-text"
          >
            {article.title}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl leading-relaxed"
          >
            {article.excerpt}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link 
              to={`/news/${article.slug}`}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all"
            >
              Ler Artigo
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <button className="p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all hover:scale-105">
              <Bookmark className="w-5 h-5" />
            </button>
            <button className="p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all hover:scale-105">
              <Share2 className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </motion.div>
      
      <div className="absolute bottom-8 right-8 z-20 flex items-center gap-2">
        {featuredArticles.map((_, index) => (
          <motion.div 
            key={index}
            className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-primary' : 'w-2 bg-white/50'}`}
            whileHover={{ scale: 1.2 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default NewsHero;